import React, { useState } from 'react';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import RoleSelectionModal from '../components/RoleSelectionModal';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRoleSelection, setShowRoleSelection] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const { login, loading } = useAdminAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!email || !password) return;

    try {
      await login(email, password, undefined, undefined);
    } catch (error: any) {
      // Check if role selection is required
      if (error.response?.status === 206 && error.response?.data?.requiresRoleSelection) {
        // Filter to only show roles that include admin access
        const adminRoles = error.response.data.availableRoles.filter((role: any) => 
          role.role === 'admin'
        );
        
        if (adminRoles.length > 0) {
          setAvailableRoles(adminRoles);
          setShowRoleSelection(true);
        } else {
          // No admin roles available
          // Error is already handled by context
        }
      }
      // Other errors are handled by the auth context
    }
  };

  const handleRoleSelection = async (selectedRole: string) => {
    setShowRoleSelection(false);
    
    try {
      await login(email, password, selectedRole, undefined);
    } catch (error) {
      console.error('Login failed:', error);
    }
  };

  const handleCancelRoleSelection = () => {
    setShowRoleSelection(false);
    setAvailableRoles([]);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-xl bg-admin-600">
            <span className="text-white font-bold text-xl">A</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Admin Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Access the ImmediateJoiner admin panel
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-admin"
                placeholder="Admin email address"
              />
            </div>
            
            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-admin pr-10"
                placeholder="Admin password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading || !email || !password}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-admin-600 hover:bg-admin-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-admin-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign in to Admin Panel'}
            </button>
          </div>
        </form>
      </div>

      <RoleSelectionModal
        isOpen={showRoleSelection}
        email={email}
        availableRoles={availableRoles}
        onSelectRole={handleRoleSelection}
        onCancel={handleCancelRoleSelection}
      />
    </div>
  );
};

export default AdminLogin;